import { useCallback, useMemo, useState } from 'react';
import { createFileRoute, Link, Outlet, useRouter } from '@tanstack/react-router';
import { BarChart3, ListTodo, Plus, Upload } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { CSVImportDialog } from '@/components/organisms/contacts/CSVImportDialog';
import { useContactImportTracker } from '@/hooks/contacts/useImportContacts';
import { ContactsWorkspaceShell, useWorkspaceHeader } from '@/components/layouts';
import { ContactTableView } from '@/components/organisms/contacts/ContactTableView';
import type { ContactPaymentsQuickAction } from '@/components/organisms/contacts/ContactPaymentMethodsSection';
import {
  cleanContactListSearch,
  coerceTagIds,
  CONTACT_MEMBERSHIP_FILTERS,
  CONTACT_SORT_OPTIONS,
  CONTACT_SOURCE_FILTERS,
  CONTACT_TYPE_FILTERS,
  DEFAULT_CONTACT_LIST_FILTERS,
  resolveContactListFilters,
  type ContactListFilters,
  type ContactMembershipFilter,
  type ContactSourceFilter,
  type ContactTypeFilter,
} from '@/components/organisms/contacts/contact-list-utils';
import { Fullbleed } from '@/contexts/fullbleed';
import { useEntityRouteState } from '@/hooks/navigation/useEntityRouteState';
import { useHasPermission } from '@/hooks/usePermissions';

type ContactRouteSearch = Partial<ContactListFilters> & {
  paymentAction?: ContactPaymentsQuickAction;
  paymentActionToken?: string;
  collectionAmount?: string;
  collectionBucket?: string;
  collectionDueDate?: string;
  collectionDaysOverdue?: string;
  collectionAsOf?: string;
  collectionReportId?: string;
};

const CONTACT_PAYMENT_ACTIONS: ContactPaymentsQuickAction[] = [
  'add-card',
  'request-card',
  'collect-payment',
];

function parsePositiveInt(value: unknown, fallback: number) {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback;
}

function parseSort(value: unknown) {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return DEFAULT_CONTACT_LIST_FILTERS.sort;
  return Math.min(Math.floor(parsed), CONTACT_SORT_OPTIONS.length - 1);
}

function parsePaymentAction(value: unknown): ContactPaymentsQuickAction | undefined {
  if (typeof value !== 'string') return undefined;
  return CONTACT_PAYMENT_ACTIONS.includes(value as ContactPaymentsQuickAction)
    ? (value as ContactPaymentsQuickAction)
    : undefined;
}

function formatCalendarDate(value: string) {
  const key = value.slice(0, 10);
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
    new Date(`${key}T00:00:00`),
  );
}

export const Route = createFileRoute('/_dashboard/dashboard/contacts')({
  validateSearch: (search: Record<string, unknown>): ContactRouteSearch => {
    const paymentAction = parsePaymentAction(search.paymentAction);

    return {
      q: typeof search.q === 'string' ? search.q : DEFAULT_CONTACT_LIST_FILTERS.q,
      type: CONTACT_TYPE_FILTERS.some((filter) => filter.value === search.type)
        ? (search.type as ContactTypeFilter)
        : DEFAULT_CONTACT_LIST_FILTERS.type,
      source: CONTACT_SOURCE_FILTERS.some((filter) => filter.value === search.source)
        ? (search.source as ContactSourceFilter)
        : DEFAULT_CONTACT_LIST_FILTERS.source,
      membership: CONTACT_MEMBERSHIP_FILTERS.some((filter) => filter.value === search.membership)
        ? (search.membership as ContactMembershipFilter)
        : DEFAULT_CONTACT_LIST_FILTERS.membership,
      customStatus:
        typeof search.customStatus === 'string'
          ? search.customStatus
          : DEFAULT_CONTACT_LIST_FILTERS.customStatus,
      tags: coerceTagIds(search.tags),
      createdFrom: typeof search.createdFrom === 'string' ? search.createdFrom : '',
      createdTo: typeof search.createdTo === 'string' ? search.createdTo : '',
      page: parsePositiveInt(search.page, DEFAULT_CONTACT_LIST_FILTERS.page),
      sort: parseSort(search.sort),
      ...(paymentAction
        ? {
            paymentAction,
            paymentActionToken:
              typeof search.paymentActionToken === 'string' ? search.paymentActionToken : '',
          }
        : {}),
      ...(typeof search.collectionReportId === 'string'
        ? {
            collectionAmount:
              typeof search.collectionAmount === 'string'
                ? search.collectionAmount
                : typeof search.collectionAmount === 'number' &&
                    Number.isFinite(search.collectionAmount)
                  ? String(search.collectionAmount)
                  : '',
            collectionBucket:
              typeof search.collectionBucket === 'string' ? search.collectionBucket : '',
            collectionDueDate:
              typeof search.collectionDueDate === 'string' ? search.collectionDueDate : '',
            collectionDaysOverdue:
              typeof search.collectionDaysOverdue === 'string'
                ? search.collectionDaysOverdue
                : typeof search.collectionDaysOverdue === 'number' &&
                    Number.isFinite(search.collectionDaysOverdue)
                  ? String(search.collectionDaysOverdue)
                  : '',
            collectionAsOf: typeof search.collectionAsOf === 'string' ? search.collectionAsOf : '',
            collectionReportId: search.collectionReportId,
          }
        : {}),
    };
  },
  component: ContactsLayout,
});

function ContactsLayout() {
  const router = useRouter();
  const routeSearch = Route.useSearch();
  const filters = useMemo(() => resolveContactListFilters(routeSearch), [routeSearch]);
  const [importOpen, setImportOpen] = useState(false);
  // A background CSV import keeps being followed after its dialog closes.
  useContactImportTracker();
  const { hasChildRoute, preservedSearch: listSearch } = useEntityRouteState({
    basePath: '/dashboard/contacts',
    search: filters,
    cleanSearch: cleanContactListSearch,
  });
  const canCreateContacts = useHasPermission('contacts:create');
  const openImport = useCallback(() => setImportOpen(true), []);

  const buildPaymentActionSearch = useCallback(
    (action: ContactPaymentsQuickAction): ContactRouteSearch => ({
      ...listSearch,
      paymentAction: action,
      paymentActionToken: String(Date.now()),
    }),
    [listSearch],
  );

  const handleSelect = useCallback(
    (contactId: string) => {
      void router.navigate({
        to: '/dashboard/contacts/$contactId',
        params: { contactId },
        search: listSearch,
      });
    },
    [listSearch, router],
  );

  const handleEdit = useCallback(
    (contactId: string) => {
      void router.navigate({
        to: '/dashboard/contacts/$contactId/edit',
        params: { contactId },
        search: listSearch,
      });
    },
    [listSearch, router],
  );

  const handleOpenPaymentsAction = useCallback(
    (contactId: string, action: ContactPaymentsQuickAction) => {
      void router.navigate({
        to: '/dashboard/contacts/$contactId',
        params: { contactId },
        search: buildPaymentActionSearch(action),
      });
    },
    [buildPaymentActionSearch, router],
  );

  const handleFiltersChange = useCallback(
    (patch: Partial<ContactListFilters>) => {
      void router.navigate({
        to: '/dashboard/contacts',
        search: cleanContactListSearch({ ...filters, ...patch }),
        replace: true,
      });
    },
    [filters, router],
  );

  // v5 model: the contact record (and its forms) render full-screen — no split,
  // no drawer. The contacts list is the full-screen table below.
  if (hasChildRoute) return <Outlet />;

  return (
    <ContactsWorkspaceShell>
      <Fullbleed />
      <ContactsHeaderSlots
        canCreate={canCreateContacts}
        listSearch={listSearch}
        onImport={openImport}
      />
      <div className="flex h-full min-h-0 flex-col bg-card">
        {routeSearch.collectionReportId ? (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-amber-700/20 bg-amber-50 px-4 py-3 text-xs text-amber-950 dark:bg-amber-950/20 dark:text-amber-100">
            <div>
              <span className="font-semibold">Collection context</span>
              {routeSearch.collectionAmount ? (
                <span className="ml-2 tabular-nums">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 0,
                  }).format(Number(routeSearch.collectionAmount))}
                </span>
              ) : null}
              {routeSearch.collectionBucket ? (
                <span className="ml-1 text-amber-900/70 dark:text-amber-100/70">
                  · {routeSearch.collectionBucket}
                </span>
              ) : null}
            </div>
            <div className="text-amber-900/70 dark:text-amber-100/70">
              {routeSearch.collectionDueDate
                ? `Oldest due ${formatCalendarDate(routeSearch.collectionDueDate)}`
                : 'Open receivable'}
              {routeSearch.collectionAsOf
                ? ` · as of ${formatCalendarDate(routeSearch.collectionAsOf)}`
                : ''}
              {routeSearch.collectionDaysOverdue
                ? ` · ${routeSearch.collectionDaysOverdue} days overdue`
                : ''}
            </div>
            <div className="ml-auto flex items-center gap-3">
              <Link
                to="/dashboard/reporting/reports/$reportId"
                params={{ reportId: routeSearch.collectionReportId }}
                className="font-medium underline underline-offset-4"
              >
                Back to collection report
              </Link>
            </div>
          </div>
        ) : null}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <ContactTableView
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onOpen={handleSelect}
            onEdit={handleEdit}
            onOpenPaymentsAction={(contact, action) => handleOpenPaymentsAction(contact.id, action)}
          />
        </div>
      </div>
      {canCreateContacts ? (
        <CSVImportDialog open={importOpen} onOpenChange={setImportOpen} />
      ) : null}
    </ContactsWorkspaceShell>
  );
}

// Injects the Add/Import actions into the global workspace header (mirrors Jobs'
// JobsNewButtonSlot) instead of a per-page title row. Renders nothing itself.
function ContactsHeaderSlots({
  canCreate,
  listSearch,
  onImport,
}: {
  canCreate: boolean;
  listSearch: ReturnType<typeof cleanContactListSearch>;
  onImport: () => void;
}) {
  const newButton = useMemo(
    () =>
      canCreate ? (
        <Button size="sm" title="Add contact" asChild>
          <Link to="/dashboard/contacts/new" search={listSearch}>
            <Plus size={14} />
            <span>
              Add<span className="hidden sm:inline"> contact</span>
            </span>
          </Link>
        </Button>
      ) : null,
    [canCreate, listSearch],
  );
  const canViewReports = useHasPermission('contacts:view');
  const quickActions = useMemo(
    () => (
      <>
        {canViewReports ? (
          <Button size="sm" variant="outline" title="Customer reports" asChild>
            <Link to="/dashboard/contacts/reports" search={(prev) => prev as never}>
              <BarChart3 size={14} />
              Reports
            </Link>
          </Button>
        ) : null}
        {canViewReports ? (
          <Button size="sm" variant="outline" asChild>
            <Link to="/dashboard/contacts/tasks">
              <ListTodo size={14} />
              Tasks
            </Link>
          </Button>
        ) : null}
        {canCreate ? (
          // Import is desktop-first (CSV upload) — hidden on mobile to keep the
          // workspace header from overflowing.
          <Button
            size="sm"
            variant="outline"
            title="Import contacts"
            onClick={onImport}
            className="hidden sm:inline-flex"
          >
            <Upload size={14} />
            Import
          </Button>
        ) : null}
      </>
    ),
    [canCreate, canViewReports, onImport],
  );

  useWorkspaceHeader({ newButton, quickActions }, [newButton, quickActions]);
  return null;
}
